/**
 * Conversion Setup Wizard.
 *
 * Bare-bones C.65 ceremony: 3 steps — Type → Details → Code. No install-method
 * choice. No GTM walkthrough. No verify-installation. The wizard creates a
 * `WEBPAGE` ConversionAction via the Google Ads API and surfaces the resulting
 * gtag snippets to the user. That's it.
 *
 * See `docs/`-level plan for the strategic context: this wizard is paper
 * compliance only; production tracking flows through the offline-conversion
 * upload path (Data Manager API) at `OfflineConversionUpload/`.
 */
import React, { useRef, useState } from 'react';
import { Button } from '@/components/ui/Button';
import { useResetScrollOnStepChange } from '@/hooks/useResetScrollOnStepChange';
import { cn } from '@/utils/cn';
import apiClient from '@/services/api/client';

import {
  ConversionSetupWizardProps,
  ConversionFormData,
  ConversionFormErrors,
  CreateConversionResponse,
  TagSnippet,
  DEFAULT_FORM_DATA,
  WIZARD_STEPS,
} from './types';

import { ChooseConversionTypeStep } from './steps/ChooseConversionTypeStep';
import { ConversionDetailsStep } from './steps/ConversionDetailsStep';
import { InstallationStep } from './steps/InstallationStep';

const FINAL_STEP = WIZARD_STEPS.length; // 3 — the code/result step

export const ConversionSetupWizard: React.FC<ConversionSetupWizardProps> = ({
  isOpen,
  onClose,
  googleAdsAccountId,
  onSuccess,
}) => {
  const [currentStep, setCurrentStep] = useState(1);
  const scrollBodyRef = useRef<HTMLDivElement>(null);
  useResetScrollOnStepChange(currentStep, scrollBodyRef);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [formData, setFormData] = useState<ConversionFormData>(DEFAULT_FORM_DATA);
  const [errors, setErrors] = useState<ConversionFormErrors>({});

  // Result from API after creating conversion
  const [conversionResult, setConversionResult] = useState<{
    conversionIdWithPrefix: string;  // e.g., 'AW-5543200460'
    conversionLabel: string;
    tagSnippets: TagSnippet[];
  } | null>(null);

  if (!isOpen) return null;

  const validateStep = (step: number): boolean => {
    const newErrors: ConversionFormErrors = {};

    switch (step) {
      case 1:
        // Category always has a default selection.
        break;

      case 2:
        if (!formData.name.trim()) {
          newErrors.name = 'Conversion name is required';
        } else if (formData.name.length > 100) {
          newErrors.name = 'Name must be 100 characters or less';
        }

        if (formData.useDefaultValue) {
          const rawValue = (formData.defaultValue ?? '').toString().trim();
          if (!rawValue) {
            newErrors.defaultValue = 'Please enter a default value';
          } else {
            const value = parseFloat(rawValue);
            if (isNaN(value) || value < 0) {
              newErrors.defaultValue = 'Please enter a valid positive number';
            }
          }
        }
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = async () => {
    if (!validateStep(currentStep)) return;

    // Final form step is step 2 (Details). After that, hit the API and
    // transition to the result screen (step 3 — Code).
    if (currentStep === 2 && !conversionResult) {
      await createConversionAction();
      return;
    }

    setCurrentStep((prev) => Math.min(prev + 1, FINAL_STEP));
  };

  const handlePrevious = () => {
    setSubmitError(null);
    setCurrentStep((prev) => Math.max(prev - 1, 1));
  };

  const createConversionAction = async () => {
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const payload = {
        google_ads_account_id: googleAdsAccountId,
        name: formData.name.trim(),
        type: 'WEBPAGE' as const,
        category: formData.category,
        default_value: formData.useDefaultValue && formData.defaultValue
          ? parseFloat(formData.defaultValue)
          : null,
        always_use_default_value: formData.useDefaultValue,
        counting_type: formData.countingType,
        click_through_lookback_days: 30,
        view_through_lookback_days: 1,
      };

      const response = await apiClient.post<CreateConversionResponse>(
        '/conversions/create',
        payload
      );

      if (response.data.success) {
        // Extract conversion ID and label from the tag snippets.
        // send_to value format: 'AW-XXXXXXXXX/LABEL'
        let conversionIdWithPrefix: string | null = null;
        let conversionLabel: string | null = null;

        if (response.data.tag_snippets?.[0]?.event_snippet) {
          const match = response.data.tag_snippets[0].event_snippet.match(
            /send_to['":\s]+['"]([^'"]+)['"]/
          );
          if (match) {
            const sendToValue = match[1];
            const parts = sendToValue.split('/');
            if (parts.length >= 1) conversionIdWithPrefix = parts[0];
            if (parts.length >= 2) conversionLabel = parts[1];
          }
        }

        // Fallback: extract from global_site_tag URL if event_snippet parsing failed.
        if (!conversionIdWithPrefix && response.data.tag_snippets?.[0]?.global_site_tag) {
          const gtagMatch = response.data.tag_snippets[0].global_site_tag.match(
            /gtag\/js\?id=(AW-\d+)/
          );
          if (gtagMatch) conversionIdWithPrefix = gtagMatch[1];
        }

        if (!conversionIdWithPrefix || !conversionLabel) {
          throw new Error(
            'Conversion action created but could not extract conversion ID/label from response. ' +
            'Find these values in Google Ads: Goals → Conversions → click your conversion → Tag setup.'
          );
        }

        setConversionResult({
          conversionIdWithPrefix,
          conversionLabel,
          tagSnippets: response.data.tag_snippets || [],
        });

        setCurrentStep(FINAL_STEP);
      } else {
        throw new Error('Failed to create conversion action');
      }
    } catch (error: unknown) {
      const apiError = error as {
        response?: { data?: { detail?: string; message?: string; error?: string } };
        message?: string;
      };

      let errorMessage = 'Failed to create conversion action. Please try again.';

      if (apiError.response?.data) {
        const data = apiError.response.data;
        errorMessage = data.detail || data.message || data.error || errorMessage;
        if (typeof errorMessage === 'object') {
          errorMessage = JSON.stringify(errorMessage);
        }
      } else if (apiError.message) {
        errorMessage = apiError.message;
      }

      setSubmitError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setCurrentStep(1);
    setFormData(DEFAULT_FORM_DATA);
    setErrors({});
    setSubmitError(null);
    setConversionResult(null);
    onClose();
  };

  const handleDone = () => {
    onSuccess?.();
    handleClose();
  };

  const updateFormData = <K extends keyof ConversionFormData>(
    field: K,
    value: ConversionFormData[K]
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (field in errors) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto"
      role="dialog"
      aria-modal="true"
      aria-labelledby="conversion-setup-wizard-title"
    >
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full my-8 relative">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h2 id="conversion-setup-wizard-title" className="text-xl font-bold text-gray-900">Set Up Conversion Tracking</h2>
            <p className="text-sm text-gray-500 mt-1">
              Track actions that matter to your business
            </p>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors p-1"
            aria-label="Close"
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Progress indicator */}
        <div className="px-6 py-4 border-b border-gray-100 bg-gray-50">
          <div className="flex items-center justify-between">
            {WIZARD_STEPS.map((step, index) => {
              const stepNumber = index + 1;
              const isCompleted = currentStep > stepNumber;
              const isCurrent = currentStep === stepNumber;

              return (
                <React.Fragment key={step.label}>
                  <div className="flex flex-col items-center">
                    <div
                      className={cn(
                        'w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors',
                        {
                          'bg-blue-600 text-white': isCurrent,
                          'bg-green-500 text-white': isCompleted,
                          'bg-gray-200 text-gray-500': !isCurrent && !isCompleted,
                        }
                      )}
                    >
                      {isCompleted ? (
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                      ) : (
                        stepNumber
                      )}
                    </div>
                    <span
                      className={cn('text-xs mt-1 font-medium', {
                        'text-blue-600': isCurrent,
                        'text-green-600': isCompleted,
                        'text-gray-400': !isCurrent && !isCompleted,
                      })}
                    >
                      {step.label}
                    </span>
                  </div>
                  {index < WIZARD_STEPS.length - 1 && (
                    <div
                      className={cn('flex-1 h-0.5 mx-2', {
                        'bg-green-500': isCompleted,
                        'bg-gray-200': !isCompleted,
                      })}
                    />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>

        {/* Step content */}
        <div ref={scrollBodyRef} className="px-6 py-6 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 350px)' }}>
          {currentStep === 1 && (
            <ChooseConversionTypeStep
              category={formData.category}
              onChangeCategory={(category) => updateFormData('category', category)}
            />
          )}

          {currentStep === 2 && (
            <ConversionDetailsStep
              name={formData.name}
              defaultValue={formData.defaultValue}
              useDefaultValue={formData.useDefaultValue}
              countingType={formData.countingType}
              onChangeName={(name) => updateFormData('name', name)}
              onChangeDefaultValue={(value) => updateFormData('defaultValue', value)}
              onChangeUseDefaultValue={(use) => updateFormData('useDefaultValue', use)}
              onChangeCountingType={(type) => updateFormData('countingType', type)}
              errors={errors}
            />
          )}

          {currentStep === FINAL_STEP && conversionResult && (
            <InstallationStep
              conversionIdWithPrefix={conversionResult.conversionIdWithPrefix}
              conversionLabel={conversionResult.conversionLabel}
              tagSnippets={conversionResult.tagSnippets}
              conversionName={formData.name}
            />
          )}

          {/* Error display */}
          {submitError && (
            <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-600">{submitError}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-between items-center">
          <div>
            {currentStep > 1 && currentStep < FINAL_STEP && (
              <Button
                variant="ghost"
                onClick={handlePrevious}
                disabled={isSubmitting}
              >
                Back
              </Button>
            )}
          </div>
          <div className="flex gap-3">
            {currentStep < FINAL_STEP ? (
              <Button
                variant="primary"
                onClick={handleNext}
                loading={isSubmitting}
                disabled={isSubmitting}
              >
                {currentStep === 2 ? 'Create & Get Code' : 'Continue'}
              </Button>
            ) : (
              <Button variant="primary" onClick={handleDone}>
                Done
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConversionSetupWizard;
